import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Archive,
  RotateCcw,
  Trash2,
  QrCode,
  LogOut,
  LogIn,
  MoreHorizontal,
  Loader2,
  Nfc,
  Unlink,
  MapPin,
  FileDown,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Badge } from '@/app/components/ui/badge';
import { Skeleton } from '@/app/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/app/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/app/components/ui/alert-dialog';
import { Card, CardContent } from '@/app/components/ui/card';
import { Alert, AlertDescription } from '@/app/components/ui/alert';
import { Switch } from '@/app/components/ui/switch';
import { Label } from '@/app/components/ui/label';
import { AssetTabs } from '@/components/assets/asset-tabs';
import { ReportProblemDialog } from '@/components/assets/report-problem-dialog';
import {
  useAsset,
  findAssetInCachedLists,
  useRetireAsset,
  useUnretireAsset,
  useDeleteAsset,
  useGenerateQRCode,
  useLinkNfcTag,
  useUnlinkNfcTag,
  useToggleInstalledInPlace,
  useExportChainOfCustody,
} from '@/hooks/use-assets';
import { toast } from '@/app/components/ui/simple-toaster';
import { handleApiError } from '@/lib/api-client';
import { NfcScanner } from '@/components/scanner/nfc-scanner';
import { isNfcSupported } from '@/hooks/use-nfc';
import { useKitsForAsset } from '@/hooks/use-kits';
import { usePermissions } from '@/hooks/use-permissions';
import { useOrgToday } from '@/hooks/use-settings';
import { PageBreadcrumb } from '@/app/components/page-breadcrumb';
import type { AssetStatus } from '@/lib/types';

const statusColors: Record<AssetStatus, string> = {
  active: 'bg-green-100 text-green-800',
  available: 'bg-green-100 text-green-800',
  deployed: 'bg-blue-100 text-blue-800',
  in_use: 'bg-blue-100 text-blue-800',
  damaged: 'bg-orange-100 text-orange-800',
  retired: 'bg-gray-100 text-gray-800',
  lost: 'bg-red-100 text-red-800',
  quarantined: 'bg-red-100 text-red-800',
};

export function AssetDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const orgToday = useOrgToday();
  const assetId = parseInt(id || '0', 10);
  const { canCreateDeployments, canEditAssets, canDeleteAssets } = usePermissions();

    const { data: loadedAsset, isLoading, isError, refetch } = useAsset(assetId);
    const cachedListAsset = findAssetInCachedLists(queryClient, assetId);
    const asset = loadedAsset ?? cachedListAsset;
    const isUsingCachedAsset = !loadedAsset && !!cachedListAsset;
    const { data: assetKits } = useKitsForAsset(assetId);
    const retireMutation = useRetireAsset();
    const unretireMutation = useUnretireAsset();
    const deleteMutation = useDeleteAsset();
    const linkNfcMutation = useLinkNfcTag();
    const unlinkNfcMutation = useUnlinkNfcTag();
    const toggleInstalledMutation = useToggleInstalledInPlace();
    const exportCocMutation = useExportChainOfCustody();

  const [showQRDialog, setShowQRDialog] = useState(false);

  const [showReportProblemDialog, setShowReportProblemDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showRetireDialog, setShowRetireDialog] = useState(false);
  const [showNfcScanner, setShowNfcScanner] = useState(false);
  const [showUnlinkDialog, setShowUnlinkDialog] = useState(false);

  const handleRetire = async () => {
    await retireMutation.mutateAsync(assetId);
    setShowRetireDialog(false);
  };

  const handleRestore = async () => {
    try {
      await unretireMutation.mutateAsync(assetId);
      toast.success('Asset returned to service');
    } catch (error) {
      toast.error(handleApiError(error).message);
    }
  };

  const handleDelete = async () => {
    await deleteMutation.mutateAsync(assetId);
    navigate('/assets');
  };

  const handleNfcScan = async (tagId: string) => {
    try {
      await linkNfcMutation.mutateAsync({ id: assetId, nfc_tag_id: tagId });
      setShowNfcScanner(false);
    } catch {
      // error handled by mutation state
    }
  };

  const handleUnlinkNfc = async () => {
    await unlinkNfcMutation.mutateAsync(assetId);
    setShowUnlinkDialog(false);
  };

  if (isLoading && !asset) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <div className="space-y-2">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-32" />
          </div>
        </div>
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  if (!asset) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="text-center py-12">
          <p className="text-red-600 mb-4">Failed to load asset details.</p>
          <Button asChild variant="outline">
            <Link to="/assets">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Assets
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      {isError && cachedListAsset && (
        <Alert variant="destructive">
          <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
            <span>Live asset details are unavailable. Showing cached data from the Assets list.</span>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}
      <PageBreadcrumb items={[
        { label: 'Assets', href: '/assets' },
        { label: asset.display_name },
      ]} />
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button asChild variant="ghost" size="icon">
            <Link to="/assets">
              <ArrowLeft className="h-5 w-5" />
            </Link>
          </Button>
          <div>
                        <div className="flex items-center gap-3">
                          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{asset.display_name}</h1>
                          <Badge className={statusColors[asset.status]}>{asset.status}</Badge>
                          {asset.is_overdue && <Badge variant="destructive">Overdue</Badge>}
                        </div>
                        <p className="text-gray-600 font-mono">{asset.serial_number}</p>
                        {assetKits && assetKits.length > 0 && (
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-sm text-gray-500">Kits:</span>
                            {assetKits.map((kit) => (
                              <Link
                                key={kit.id}
                                to={`/kits/${kit.id}`}
                                className="text-sm text-blue-600 hover:underline"
                              >
                                {kit.name}
                              </Link>
                            ))}
                          </div>
                        )}
          </div>
        </div>

        {!isUsingCachedAsset && (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="destructive"
              className="h-12 w-full text-base sm:h-10 sm:w-auto sm:text-sm"
              onClick={() => setShowReportProblemDialog(true)}
            >
              <AlertTriangle className="h-5 w-5 mr-2 sm:h-4 sm:w-4" />
              Report a problem
            </Button>

            <Button variant="outline" onClick={() => setShowQRDialog(true)}>
              <QrCode className="h-4 w-4 mr-2" />
              QR Code
            </Button>

            {asset.nfc_tag_id ? (
              <Button variant="outline" onClick={() => setShowUnlinkDialog(true)}>
                <Unlink className="h-4 w-4 mr-2" />
                NFC: {asset.nfc_tag_id.slice(0, 8)}...
              </Button>
            ) : (
              isNfcSupported() && (
                <Button variant="outline" onClick={() => setShowNfcScanner(true)}>
                  <Nfc className="h-4 w-4 mr-2" />
                  Link NFC
                </Button>
              )
            )}

            {(asset.status === 'available' || asset.status === 'active') &&
              canCreateDeployments && (
                <Button asChild variant="outline">
                  <Link to={`/deployments/check-out?asset=${asset.id}`}>
                    <LogOut className="h-4 w-4 mr-2" />
                    Check Out
                  </Link>
                </Button>
              )}

            {(asset.status === 'deployed' ||
              asset.status === 'in_use' ||
              asset.out_of_service_checkout_open) &&
              canCreateDeployments && (
                <Button asChild variant="outline">
                  <Link to={`/deployments/check-in?asset=${asset.id}`}>
                    <LogIn className="h-4 w-4 mr-2" />
                    Check In
                  </Link>
                </Button>
              )}

            {canEditAssets && (
              <Button asChild>
                <Link to={`/assets/${assetId}/edit`}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Link>
              </Button>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  disabled={exportCocMutation.isPending}
                  onClick={() => {
                    exportCocMutation.mutate(assetId, {
                      onSuccess: ({ blob, filename }) => {
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = filename;
                        a.click();
                        window.URL.revokeObjectURL(url);
                      },
                    });
                  }}
                >
                  <FileDown className="h-4 w-4 mr-2" />
                  {exportCocMutation.isPending ? 'Exporting...' : 'Export History'}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {canEditAssets &&
                  (asset.status === 'retired' ? (
                    <DropdownMenuItem onClick={handleRestore} disabled={unretireMutation.isPending}>
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Restore Asset
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem onClick={() => setShowRetireDialog(true)}>
                      <Archive className="h-4 w-4 mr-2" />
                      Retire Asset
                    </DropdownMenuItem>
                  ))}
                {canDeleteAssets && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-red-600"
                      onClick={() => setShowDeleteDialog(true)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Asset
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      {/* Installed in Place */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <MapPin className="h-5 w-5 text-gray-500" />
              <div>
                <Label htmlFor="installed-toggle" className="text-sm font-medium cursor-pointer">
                  Installed in place
                </Label>
                <p className="text-xs text-muted-foreground">
                  Mark this asset as fixed at its current location/spot
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {asset.is_installed && asset.installed_at && (
                <span className="text-xs text-muted-foreground">
                  Since {asset.installed_at}
                </span>
              )}
              <Switch
                id="installed-toggle"
                checked={asset.is_installed}
                disabled={isUsingCachedAsset || toggleInstalledMutation.isPending}
                onCheckedChange={(checked) => {
                  toggleInstalledMutation.mutate({
                    id: assetId,
                    is_installed: checked,
                    installed_at: checked ? orgToday : null,
                  });
                }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <AssetTabs asset={asset} readOnly={isUsingCachedAsset} />

      {!isUsingCachedAsset && (
        <ReportProblemDialog
          asset={asset}
          open={showReportProblemDialog}
          onOpenChange={setShowReportProblemDialog}
        />
      )}

      {!isUsingCachedAsset && (
        <QRCodeDialog
          assetId={assetId}
          assetName={asset.display_name}
          open={showQRDialog}
          onOpenChange={setShowQRDialog}
        />
      )}

      <AlertDialog open={showRetireDialog} onOpenChange={setShowRetireDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Retire Asset</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to retire "{asset.display_name}"? This will mark the asset as no longer
              in service. You can restore it later if needed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRetire} disabled={retireMutation.isPending}>
              {retireMutation.isPending ? 'Retiring...' : 'Retire Asset'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={showNfcScanner} onOpenChange={setShowNfcScanner}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Link NFC Tag</DialogTitle>
            <DialogDescription>
              Hold an NFC tag near your device to link it to {asset.display_name}
            </DialogDescription>
          </DialogHeader>
          <NfcScanner
            onScan={handleNfcScan}
            onError={() => {}}
            onClose={() => setShowNfcScanner(false)}
          />
          {linkNfcMutation.isError && (
            <p className="text-sm text-red-600 text-center">
              {(linkNfcMutation.error as Error)?.message || 'Failed to link NFC tag'}
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNfcScanner(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showUnlinkDialog} onOpenChange={setShowUnlinkDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unlink NFC Tag</AlertDialogTitle>
            <AlertDialogDescription>
              Remove NFC tag "{asset.nfc_tag_id}" from "{asset.display_name}"? You can link a new tag later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleUnlinkNfc} disabled={unlinkNfcMutation.isPending}>
              {unlinkNfcMutation.isPending ? 'Unlinking...' : 'Unlink Tag'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Asset</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{asset.display_name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete Asset'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function QRCodeDialog({
  assetId,
  assetName,
  open,
  onOpenChange,
}: {
  assetId: number;
  assetName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { data: qrCode, isLoading, isError } = useGenerateQRCode(assetId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>QR Code</DialogTitle>
          <DialogDescription>Scan this QR code to quickly access {assetName}</DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-center p-6">
          {isLoading ? (
            <Loader2 className="h-12 w-12 animate-spin text-gray-400" />
          ) : isError ? (
            <p className="text-red-600">Failed to generate QR code</p>
          ) : qrCode ? (
            <img src={qrCode} alt={`QR code for ${assetName}`} className="max-w-full" />
          ) : (
            <p className="text-gray-500">No QR code available</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {qrCode && (
            <Button
              onClick={() => {
                const link = document.createElement('a');
                link.href = qrCode;
                link.download = `${assetName}-qr.png`;
                link.click();
              }}
            >
              Download
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
